import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import {
  PasswordPolicy,
  ProviderAuthSettings,
  UpdateProviderAuthSettingsInput,
  CustomerAuthSettings,
  UpdateCustomerAuthSettingsInput,
} from '@/types/provider-auth-settings';

const DEFAULT_POLICY: PasswordPolicy = {
  minLength: 12,
  requireNumbers: true,
  requireSymbols: true,
  requireMixedCase: true,
  disallowPrevious: 5,
};

type ApiPayload = {
  data?: {
    password_login_enabled?: unknown;
    password_policy?: Record<string, unknown>;
  };
};

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

function normalizePolicy(policy?: Record<string, unknown>): PasswordPolicy {
  if (!policy) {
    return { ...DEFAULT_POLICY };
  }

  return {
    minLength: readNumber(policy.min_length ?? policy.minLength) ?? DEFAULT_POLICY.minLength,
    requireNumbers:
      readBoolean(policy.require_numbers ?? policy.requireNumbers) ??
      DEFAULT_POLICY.requireNumbers,
    requireSymbols:
      readBoolean(policy.require_symbols ?? policy.requireSymbols) ??
      DEFAULT_POLICY.requireSymbols,
    requireMixedCase:
      readBoolean(policy.require_mixed_case ?? policy.requireMixedCase) ??
      DEFAULT_POLICY.requireMixedCase,
    disallowPrevious:
      readNumber(policy.disallow_previous ?? policy.disallowPrevious) ??
      DEFAULT_POLICY.disallowPrevious,
  };
}

function formatPolicy(policy: Partial<PasswordPolicy> | null | undefined) {
  if (!policy) {
    return undefined;
  }

  const payload: Record<string, unknown> = {};

  if (typeof policy.minLength === 'number') {
    payload.min_length = policy.minLength;
  }
  if (typeof policy.disallowPrevious === 'number') {
    payload.disallow_previous = policy.disallowPrevious;
  }
  if (typeof policy.requireNumbers === 'boolean') {
    payload.require_numbers = policy.requireNumbers;
  }
  if (typeof policy.requireSymbols === 'boolean') {
    payload.require_symbols = policy.requireSymbols;
  }
  if (typeof policy.requireMixedCase === 'boolean') {
    payload.require_mixed_case = policy.requireMixedCase;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
}

export class ProviderAuthSettingsRequestError extends ProviderSettingsRequestError {}

async function requestAuthSettings(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, {
      method: options.method,
      body: options.body,
    })) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderAuthSettingsRequestError(error.message, error.status, error.payload);
    }
    throw error;
  }
}

class ProviderAuthSettingsService {
  async getAuthSettings(): Promise<ProviderAuthSettings> {
    const response = await requestAuthSettings('/api/v1/provider/settings/auth');
    const payload = response.data ?? {};

    const passwordLoginEnabled = readBoolean(payload.password_login_enabled) ?? false;

    return {
      passwordLoginEnabled,
      passwordPolicy: normalizePolicy(payload.password_policy),
    };
  }

  async updateAuthSettings(
    input: UpdateProviderAuthSettingsInput,
  ): Promise<ProviderAuthSettings> {
    const body: Record<string, unknown> = {
      password_login_enabled: input.passwordLoginEnabled,
    };

    const formattedPolicy = formatPolicy(input.passwordPolicy);
    if (formattedPolicy) {
      body.password_policy = formattedPolicy;
    }

    const response = await requestAuthSettings('/api/v1/provider/settings/auth', {
      method: 'PUT',
      body,
    });

    const payload = response.data ?? {};
    const passwordLoginEnabled =
      readBoolean(payload.password_login_enabled) ?? input.passwordLoginEnabled;

    return {
      passwordLoginEnabled,
      passwordPolicy: normalizePolicy(payload.password_policy),
    };
  }

  async getCustomerAuthSettings(): Promise<CustomerAuthSettings> {
    const response = await requestAuthSettings('/api/v1/provider/settings/customer-auth');
    const payload = response.data ?? {};

    return {
      passwordLoginEnabled: readBoolean(payload.password_login_enabled) ?? false,
      passwordPolicy: normalizePolicy(payload.password_policy),
    };
  }

  async updateCustomerAuthSettings(
    input: UpdateCustomerAuthSettingsInput,
  ): Promise<CustomerAuthSettings> {
    const body: Record<string, unknown> = {
      password_login_enabled: input.passwordLoginEnabled,
    };
    const formattedPolicy = formatPolicy(input.passwordPolicy);
    if (formattedPolicy) {
      body.password_policy = formattedPolicy;
    }

    const response = await requestAuthSettings('/api/v1/provider/settings/customer-auth', {
      method: 'PUT',
      body,
    });
    const payload = response.data ?? {};
    return {
      passwordLoginEnabled:
        readBoolean(payload.password_login_enabled) ?? input.passwordLoginEnabled,
      passwordPolicy: normalizePolicy(payload.password_policy),
    };
  }
}

export const providerAuthSettingsService = new ProviderAuthSettingsService();
